import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../../lib/supabaseClient";

const Watergoal = () => {
  const TARGET = 10;
  const ML_PER_GLASS = 250;

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchWater = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        setUserId(user.id);
        const { data } = await supabase.from("daily_activity").select("water").eq("user_id", user.id).eq("date", today).maybeSingle();
        if (data) setCount(data.water || 0);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetchWater();
  }, [today]);

  const updateWater = async (delta) => {
    if (!userId) return;
    setCount((prev) => {
      const newCount = Math.max(0, Math.min(20, prev + delta));
      supabase.from("daily_activity").upsert({ user_id: userId, date: today, water: newCount }, { onConflict: "user_id,date" })
        .then(({ error }) => { if (error) setCount(prev); });
      return newCount;
    });
  };

  if (loading) return <div style={styles.loader}>Chilling the water...</div>;

  const progress = Math.min((count / TARGET) * 100, 100);
  
  // Dynamic status messages
  const getStatus = () => {
    if (count === 0) return "Start your day with a glass!";
    if (progress < 50) return "Great start, keep sipping!";
    if (progress < 100) return "Almost there, stay hydrated!";
    return "Hydration Goal Reached! 🏆";
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerTop}>
            <h2 style={styles.title}>AquaTrack</h2>
            
          </div>
          <p style={styles.statusMsg}>{getStatus()}</p>
        </header>

        {/* Liquid Progress Card */}
        <section style={styles.liquidCard}>
          {/* Animated Water Background */}
          <div style={{ ...styles.waterFill, height: `${progress}%` }}>
            <div style={styles.wave} />
          </div>

          <div style={styles.cardContent}>
            <div style={styles.mainDisplay}>
              <span style={styles.bigNumber}>{count}</span>
              <span style={styles.unit}>/ {TARGET} glasses</span>
            </div>
            
            <div style={styles.mlBadge}>
                {(count * ML_PER_GLASS).toLocaleString()} ml total
            </div>
          </div>
        </section>

        {/* Quick Stats Grid */}
        <div style={styles.statsGrid}>
          <div style={styles.smallStat}>
            <span style={styles.statIcon}>💧</span>
            <div>
              <p style={styles.statLabel}>Next Goal</p>
              <p style={styles.statInfo}>{progress >= 100 ? "Done" : "1 glass"}</p>
            </div>
          </div>
          <div style={styles.smallStat}>
            <span style={styles.statIcon}>⏲️</span>
            <div>
              <p style={styles.statLabel}>Last Drink</p>
              <p style={styles.statInfo}>Just now</p>
            </div>
          </div>
        </div>

        {/* High-Performance Controls */}
        <section style={styles.controls}>
          <button style={styles.circleBtn} onClick={() => updateWater(-1)}>–</button>
          <button style={styles.mainDrinkBtn} onClick={() => updateWater(1)}>
            <div style={styles.btnRipple} />
            Add Glass
          </button>
          <button style={styles.circleBtn} onClick={() => updateWater(1)}>+</button>
        </section>

        <p style={styles.footerInfo}>Recommended: 2.5L daily for your profile</p>
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: "100vh", background: "#f0f4f8", padding: "30px 20px", fontFamily: "'Inter', sans-serif" },
  container: { maxWidth: "380px", margin: "0 auto" },
  loader: { textAlign: "center", marginTop: "100px", color: "#0ea5e9" },
  
  header: { marginBottom: "30px" },
  headerTop: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { fontSize: "28px", fontWeight: "900", color: "#1e293b", margin: 0, letterSpacing: "-1px" },
  streakBadge: { background: "#ffffff", padding: "6px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "700", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" },
  statusMsg: { color: "#64748b", fontSize: "15px", marginTop: "6px", fontWeight: "500" },

  liquidCard: {
    height: "260px", background: "#ffffff", borderRadius: "35px", position: "relative",
    overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 20px 40px -15px rgba(14, 165, 233, 0.15)", border: "1px solid #ffffff"
  },
  waterFill: {
    position: "absolute", bottom: 0, left: 0, right: 0, 
    background: "linear-gradient(180deg, #38bdf8 0%, #0284c7 100%)",
    transition: "height 1.5s cubic-bezier(0.19, 1, 0.22, 1)",
    zIndex: 1
  },
  cardContent: { zIndex: 2, textAlign: "center" },
  bigNumber: { fontSize: "80px", fontWeight: "900", color: "#1e293b", display: "block", lineHeight: "1" },
  unit: { fontSize: "16px", fontWeight: "700", color: "#64748b", marginTop: "8px" },
  mlBadge: { 
    marginTop: "15px", background: "rgba(255,255,255,0.8)", padding: "6px 16px", 
    borderRadius: "20px", fontSize: "13px", fontWeight: "800", color: "#0369a1",
    backdropFilter: "blur(4px)"
  },

  statsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginTop: "20px" },
  smallStat: { background: "#ffffff", padding: "15px", borderRadius: "20px", display: "flex", gap: "12px", alignItems: "center" },
  statIcon: { fontSize: "20px" },
  statLabel: { margin: 0, fontSize: "11px", color: "#94a3b8", fontWeight: "700", textTransform: "uppercase" },
  statInfo: { margin: 0, fontSize: "14px", fontWeight: "700", color: "#334155" },

  controls: { display: "flex", gap: "15px", marginTop: "25px", alignItems: "center" },
  mainDrinkBtn: {
    flex: 2, height: "65px", borderRadius: "22px", border: "none", 
    background: "#1e293b", color: "#ffffff", fontWeight: "800", fontSize: "16px",
    cursor: "pointer", boxShadow: "0 10px 20px rgba(0,0,0,0.1)", position: "relative", overflow: "hidden"
  },
  circleBtn: {
    width: "65px", height: "65px", borderRadius: "22px", border: "none",
    background: "#ffffff", color: "#1e293b", fontSize: "24px", fontWeight: "300",
    cursor: "pointer", boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
  },
  footerInfo: { textAlign: "center", marginTop: "25px", fontSize: "12px", color: "#94a3b8", fontWeight: "500" }
};

export default Watergoal;